import React, { useState, useEffect, useRef } from "react";
import { ListPageSkeleton } from "../../Components/Skeleton";
import SearchBar from "../../Components/SearchBar";
import Button from "../../Components/Button";
import { IoMdAdd } from "react-icons/io";
import { Modal } from "../../Components/Modal";
import { supabase } from "../../supabaseClient";
import { MdModeEditOutline, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function Factory() {
  const [showModal, setShowModal] = useState(false);
  const [factoryName, setFactoryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [factories, setFactories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editFactory, setEditFactory] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Fetch all factories
  const fetchFactories = async () => {
    setIsLoading(true);
    const { data, error } = await supabase.from("Factory").select("*");
    if (error) {
      console.error("Error fetching factories:", error);
      setFactories([]);
    } else {
      setFactories(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchFactories();
  }, []);

  const handleDeleteFactory = async (id) => {
    try {
      const { error } = await supabase.from("Factory").delete().eq("id", id);
      if (error) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Failed to delete factory",
          showConfirmButton: false,
          timer: 3000,
          toast: true,
        });
      } else {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Factory deleted successfully",
          showConfirmButton: false,
          timer: 3000,
          toast: true,
        });
        fetchFactories();
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "An unexpected error occurred",
        showConfirmButton: false,
        timer: 3000,
        toast: true,
      });
    }
  };

  useEffect(() => {
    if (showModal && inputRef.current) inputRef.current.focus();
  }, [showModal]);

  // Debounce hook
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
  }

  // Search filtering
  useEffect(() => {
    const fetchFilteredFactories = async () => {
      setIsLoading(true);

      let query = supabase.from("Factory").select("*");

      if (debouncedSearch.trim() !== "") {
        query = query.ilike("name", `%${debouncedSearch}%`);
      }

      const { data, error } = await query;
      if (error) {
        setFactories([]);
      } else {
        setFactories(data);
      }

      setIsLoading(false);
    };

    fetchFilteredFactories();
  }, [debouncedSearch]);

  return (
    <div className="h-screen p-6 bg-white">
      {/* Controls */}
      <div className="flex flex-wrap justify-center items-center mb-4 gap-4">
        <div className="w-full sm:w-3/4 md:w-2/3 lg:w-1/3">
          <SearchBar
            placeholder="Search Factory"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputClassName="py-3"
          />
        </div>

        <Button
          variant="primary"
          onClick={() => navigate("/factory/create")}
          className="px-4 text-sm flex justify-between items-center gap-3"
        >
          <IoMdAdd />
          Add Factory
        </Button>
      </div>

      {/* Factories List */}
      <div className="w-full mx-auto border p-4 h-[calc(100vh-7rem)] overflow-auto rounded-lg bg-white">
        <h1 className="text-3xl font-semibold uppercase font-serif border-b pb-3 mb-4">
          Factories
        </h1>

        {isLoading ? (
          <ListPageSkeleton count={8} />
        ) : factories.length === 0 ? (
          <p className="text-center font-semibold text-red-500">
            No data found
          </p>
        ) : (
          factories.map((item) => (
            <div
              key={item.id}
              onClick={(e) => {
                const isButton = e.target.closest("button");
                if (!isButton) {
                  navigate(`/tile?factory=${encodeURIComponent(item.id)}`);
                }
              }}
              className="mb-3 bg-white px-4 py-2 rounded-lg border border-gray-400 hover:shadow-lg transition"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-md font-semibold text-gray-800 truncate">
                  {item.name}
                </h3>

                <div className="flex gap-3">
                  <button
                    onClick={() => navigate(`/factory/${item.id}`)}
                    className="p-2 bg-yellow-100 text-yellow-500 rounded-full hover:bg-yellow-200"
                  >
                    <MdModeEditOutline />
                  </button>

                  <button
                    onClick={() => handleDeleteFactory(item.id)}
                    className="p-2 bg-red-100 text-red-500 rounded-full hover:bg-red-200"
                  >
                    <MdDelete />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      <Modal
        customClasses="w-full sm:max-w-lg md:max-w-2xl"
        showModal={showModal}
        setShowModal={setShowModal}
      >
        <div className="space-y-4 bg-gray-100 p-4 rounded-md border">
          <h2 className="text-xl font-semibold">
            {editFactory ? "Edit Factory" : "Add Factory"}
          </h2>

          <input
            type="text"
            ref={inputRef}
            placeholder="Enter factory name"
            value={factoryName}
            onChange={(e) => setFactoryName(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          />

          <div className="flex justify-end gap-2"></div>
        </div>
      </Modal>
    </div>
  );
}

export default Factory;
